<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>

        <template #filter_condition_options>
            <MonitorOptionSet 
                :option_set="option_set"
                @filter_data="filter_data" 
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :table_label_function="judge_visibility_table_color"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template #diagram_dialog="{ close_dialog }"> 
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :chart_type="chart_options.type"
                        :default_variable="weather_history_option"
                        :prop_rain_selected="$t('__rainfall.hour_3')"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template #table_description>
                    說明："--"標示，顯示該欄位無資料。
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import {
        redirect_monitor_table_ajax
    } from "@/utils/monitor/monitor.js"
    import { 
        sort_callback_by_city_name_tw, 
        sort_float_callback_by_given_key,  
    } from "@/utils/monitor/common_tool.js"
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
    } from '@/utils/monitor/option.js'
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { judge_value_valid } from "@/utils/chart/verification.js"
    import { TABLE_VALUE_TEXT_MAPPING, WEATHER_HISTORY_OPTION_MAPPING } from "@/config/monitor_config.js"
    import { mapState } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"

    export default {
        name: 'SurfaceWeather',

        components: {
            MonitorLayoutTemplate,
            MonitorOptionSet, 
            QpeDatetime,
            QpeDatatable, 
            GisChart: () => import('@/components/chart/gis_chart.vue'),
        },

        mixins: [monitor_mixin],

        data() {
            return {
                // 排序使用
                table_data: [],
                header: [],
                error_text: '',

                immutable_table_data: [],
                option_set: [],
                display_all_city: '所有縣市',
                display_all_township: '所有鄉鎮',

                default_value: 214,

                should_draw_diagram_header: ['風向(degree)', '平均風(m/s)', '瞬間風(m/s)', '溫度(°C)', '相對濕度(%)', '氣壓(hPa)', '10分鐘雨量(mm)', '1小時雨量(mm)'],
                should_draw_weather_diagram_header: ['風向(degree)', '平均風(m/s)', '瞬間風(m/s)', '溫度(°C)', '相對濕度(%)', '氣壓(hPa)'],
                should_draw_station_diagram_header: ['10分鐘雨量(mm)', '1小時雨量(mm)'],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true, 'weather': true }
                },
                weather_history_option: '',
                
                local_storage_option_name: 'ground_visibility_option'
            }
        },

        computed: {
            ...mapState([
                'client_name',
            ]),

            table_title() {
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            }
        },
        
        watch: {
            immutable_table_data: {
                handler() {
                    if (this.immutable_table_data) {
                        this.generate_option_set()
                        this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                    }
                },
                deep: true
            },
        },
        
        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                this.immutable_table_data = data.sort(sort_callback_by_city_name_tw);
                this.table_data = this.sort_table_data(data);
                this.header = header;
            },

            generate_option_set() {
                const basic_variable_options = [
                    { default_value: this.display_all_city, json_key: '縣市', label: 'city' },
                    { default_value: this.display_all_township, json_key: '鄉鎮', label: 'township' },
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)
                
                if (this.option_set.length === 0) {
                    this.option_set = [...variable_options]
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data(filtered_cond, change_condition_label) {
                const update_current_value_by_filtered_cond = update_current_option_value(filtered_cond)
                this.option_set = this.option_set.map(update_current_value_by_filtered_cond)
                if (change_condition_label === 'city') {
                    this.option_set[1].current_option_value = this.display_all_township
                    filtered_cond['township'] = this.display_all_township
                }

                const compare_city = filtered_cond['city'] === this.display_all_city ? '' : filtered_cond['city']
                const compare_township = filtered_cond['township'] === this.display_all_township ? '' :  filtered_cond['township']
        
                let filtered_data = this.immutable_table_data.filter((item) => {
                    return (
                        item['縣市'].includes(compare_city) &&
                        item['鄉鎮'].includes(compare_township)
                    )
                })
                this.table_data = this.sort_table_data(filtered_data)

                // 過濾完之後重新根據資料狀態，生成鄉鎮 option
                this.generate_towns_options(filtered_cond['city'])
                
                // 把目前選擇的 option 存起來
                localStorage.setItem(this.local_storage_option_name, JSON.stringify(filtered_cond))
            },

            generate_towns_options(city) {                
                const filter_towns_by_city = item => item['縣市'] === city
                const get_town = item => item['鄉鎮']
                const filtered_towns_options = city === this.display_all_city ? this.immutable_table_data : this.immutable_table_data.filter(filter_towns_by_city)
                const towns_options = filtered_towns_options.map(get_town)
                this.option_set[1].option_array = [this.display_all_township, ...new Set(towns_options)]
            },

            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.chart_options = { ...this.chart_options, ...get_stations_history_params.call(this, current_row_data) }

                const special_weather_history_option = WEATHER_HISTORY_OPTION_MAPPING[header_name]
                if (special_weather_history_option) {
                    return this.weather_history_option = special_weather_history_option
                } 
                    
                this.weather_history_option = header_name.split('(')[0].trim()
            },

            judge_visibility_table_color(item) {
                const visibility_60_sec = judge_value_valid(item['能見度60秒平均(km)']) ? parseFloat(item['能見度10分鐘平均(km)']) : 100  
                const visibility_10_min = judge_value_valid(item['能見度10分鐘平均(km)']) ? parseFloat(item['能見度10分鐘平均(km)']) : 100
                return visibility_60_sec < 1 && visibility_10_min < 1 ? 'row_head_border custom_yellow' : ''
            },

            sort_table_data(data) {
                const options = { 
                    from: 'min',
                    default_float_value: 100,
                }
                const sort_by_10min_visibility = sort_float_callback_by_given_key('能見度10分鐘平均(km)', options)
                return [...data].sort(sort_by_10min_visibility)
            }
        },
    }
</script>

<style>

</style>